/**
 * Type definitions for Irrigation System Monitoring
 * MVP Features only - no Alerts, Pressure/Temp Graphs, or Field Grouping
 */

// Auto-refresh interval for All Farm View data (5 minutes)
var ALL_FARM_VIEW_REFRESH_INTERVAL_MS = 300000;

/**
 * @typedef {'None' | 'Low' | 'Medium' | 'High'} StressLevel
 */

/**
 * @typedef {'flow_meter' | 'manual_input' | 'connect_sensor'} IrrigationSource
 */

/**
 * @typedef {'standard' | 'high_pressure'} PiezoType
 */

/**
 * @typedef {'Farm' | 'Field'} ViewMode
 */

/**
 * Irrigation Activity Data
 * @typedef {Object} IrrigationActivity
 * @property {string} fieldId
 * @property {IrrigationSource} source - Priority: flow_meter > manual_input > connect_sensor
 * @property {number} irrigationEndDate - Unix timestamp
 * @property {boolean} isIrrigating - True if within 3.5 hours from now
 * @property {string} displayText - Formatted as "Today XX:XXam/pm", "Yesterday...", or "X days ago"
 * @property {number} lastUpdated - Unix timestamp for 1-hour refresh
 */

/**
 * Weekly Performance Data
 * @typedef {Object} WeeklyPerformance
 * @property {string} fieldId
 * @property {number} actualValue - Sum of irrigation since week start (mm/inch/hours)
 * @property {number} plannedValue - DailyReturn * 7, positioned at 80% of bar width
 * @property {number} totalBarWidth - PlannedValue * 1.25, or constant 75 if no plan
 * @property {number} percentage - Actual/TotalBarWidth * 100, max 100%
 * @property {string} unit - 'mm' | 'inch' | 'hours'
 * @property {number} weekStartDate - Unix timestamp based on user's regional settings
 */

/**
 * Root Zone Water Potential Data
 * @typedef {Object} RootZoneWP
 * @property {string} fieldId
 * @property {number} value - Current water potential
 * @property {number} startDate - Unix timestamp
 * @property {string} unit
 */

/**
 * Stress Gauge Data
 * @typedef {Object} StressGauge
 * @property {string} fieldId
 * @property {StressLevel} level
 * @property {number} value - 0-1 scale
 * @property {string} color
 */

/**
 * Recommendation Data
 * @typedef {Object} Recommendation
 * @property {string} fieldId
 * @property {number} amount - Irrigation amount
 * @property {string} unit
 * @property {number} frequencyDays - Days between irrigations
 * @property {number} timesPerWeek - Alternative frequency format
 * @property {string} displayText - "[Amount] every [X] days" or "[Amount] [X] times a week"
 */

/**
 * Field Data (MVP subset)
 * @typedef {Object} FieldData
 * @property {string} id
 * @property {string} name
 * @property {Object} polygon - GeoJSON polygon
 * @property {StressLevel} stressLevel
 * @property {IrrigationSource} irrigationSource
 * @property {PiezoType} piezoType
 * @property {number} dailyReturn - For planned value calculations
 * @property {IrrigationActivity} irrigationActivity
 * @property {WeeklyPerformance} weeklyPerformance
 * @property {StressGauge} stressGauge
 * @property {RootZoneWP} rootZoneWP
 * @property {Recommendation} recommendation
 */

/**
 * Persistence State
 * @typedef {Object} PersistenceState
 * @property {ViewMode} lastViewMode - 'Farm' or 'Field'
 * @property {boolean} isAdvancedMode - On/Off
 * @property {string} lastSelectedFieldId
 * @property {string} lastSelectedFarmingUnitId
 * @property {boolean} hasSeenBanner - Onboarding banner shown
 */

/**
 * All Farm View API Response Types
 */

/**
 * Field data from All Farm View API
 * @typedef {Object} FieldAllFarmData
 * @property {string} fieldId
 * @property {string} fieldName
 * @property {number|null} weeklyPerformance - Weekly irrigation value
 * @property {number|null} weeklyGoal - Weekly irrigation goal
 * @property {boolean} isIrrigating - Whether field is currently irrigating
 * @property {string|null} lastIrrigationStartedAt - ISO timestamp of last irrigation start
 * @property {string|null} lastIrrigationEndedAt - ISO timestamp of last irrigation end
 * @property {'talgil'|'piezometer'|'manual'|'none'|null} lastIrrigationSource - Source of irrigation data
 */

/**
 * All Farm View API Response
 * @typedef {Object} AllFarmViewDataResponse
 * @property {string} farmingUnitId
 * @property {FieldAllFarmData[]} fields
 * @property {string} units - Unit of measurement ('INCH', 'MM', 'CM')
 * @property {string} dataTimestamp - ISO timestamp of when data was generated
 */

/**
 * All Farm View Hook State
 * @typedef {Object} AllFarmViewState
 * @property {AllFarmViewDataResponse|null} data
 * @property {boolean} isLoading - True during initial load
 * @property {boolean} isRefreshing - True during background refresh
 * @property {Error|null} error - Error from initial load (not refresh)
 * @property {Date|null} lastFetchedAt
 */

module.exports = {
  ALL_FARM_VIEW_REFRESH_INTERVAL_MS: ALL_FARM_VIEW_REFRESH_INTERVAL_MS,
};
